import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authAPI } from '../../services/api';
import { Link } from 'react-router-dom';

// Password validation function
const validatePassword = (password) => {
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/[A-Z]/.test(password)) return 'Must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Must contain at least one digit';
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return 'Must contain at least one special character';
  }
  return '';
};

const SetupPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [checkingToken, setCheckingToken] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    password: '',
    confirmPassword: '',
  });

  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Toggle confirm password visibility
  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle password field changes
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setError('');
    
    // Clear field errors when user types
    setFieldErrors(prev => ({
      ...prev,
      password: ''
    }));
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setError('');
    
    // Clear field errors when user types
    setFieldErrors(prev => ({
      ...prev,
      confirmPassword: ''
    }));
  };

  // Verify token when component loads
  useEffect(() => {
    const verifyToken = async () => {
      try {
        setCheckingToken(true);
        console.log("Token from URL:", token);
        
        // You could add a preliminary check here if needed
        setCheckingToken(false);
      } catch (err) {
        console.error("Error verifying token:", err);
        setError("Could not verify token. Please try again or contact support.");
        setCheckingToken(false);
      }
    };
    
    if (token) {
      verifyToken();
    } else {
      setError("No token provided. Please use the link from your email.");
      setCheckingToken(false);
    }
  }, [token]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Client-side validation
    const passwordError = validatePassword(password);
    const confirmPasswordError = password !== confirmPassword ? 'Passwords do not match' : '';
    
    if (passwordError || confirmPasswordError) {
      setFieldErrors({
        password: passwordError,
        confirmPassword: confirmPasswordError,
      });
      return;
    }
    
    setLoading(true);
    
    try {
      console.log("Submitting password setup with token:", token);
      
      // Call the API to set password
      await authAPI.setupPassword(token, password);
      setSuccess(true);
      
      // Immediately redirect to login page
      navigate('/login', { 
        state: { 
          message: 'Password set successfully. Please log in with your new password.',
          alertType: 'success'
        } 
      });
    } catch (err) {
      console.error("Error during password setup:", err);
      // Show all server errors as toast (general error)
      setError(err.response?.data?.error || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };
  
  if (checkingToken) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Verifying your setup link...
        </Typography>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          Set Your Password
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
            {token && (
              <Box sx={{ mt: 1, fontSize: '0.8rem' }}>
                <strong>Token:</strong> {token.substring(0, 10)}...{token.substring(token.length - 5)}
              </Box>
            )}
          </Alert>
        )}
        
        {success ? (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            Password set successfully! Redirecting to login page...
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password || 'Min 8 chars with uppercase, lowercase, digit & special character'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!fieldErrors.confirmPassword}
              helperText={fieldErrors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Set Password'}
            </Button>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2">
                Already have an account? <Link to="/login">Sign in</Link>
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SetupPassword; 